import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { Shell } from 'Common/Layout/Layout'
import { TenantListPage } from 'Features/Tenant/TenantList.page'
import { ProjectsPage } from 'Features/Projects/Projects.page'
import { ProjectLayout } from 'Features/Projects/ProjectLayout'
import { lazy, Suspense } from 'react'
import { CircularProgress, Box } from '@mui/material'

// Lazy load main navigation components
const ScenariosView = lazy(() =>
  import('Features/Projects/Scenarios/Scenarios.View').then((module) => ({
    default: module.ScenariosView,
  }))
)
const AnalysisView = lazy(() =>
  import('Features/Projects/Analysis/Analysis.View').then((module) => ({
    default: module.AnalysisView,
  }))
)
const ApprovalView = lazy(() =>
  import('Features/Projects/Approval/Approval.View').then((module) => ({
    default: module.ApprovalView,
  }))
)
const SnapshotView = lazy(() =>
  import('Features/Projects/Snapshot/Snapshot.View').then((module) => ({
    default: module.SnapshotView,
  }))
)

// Loading component for lazy loaded routes
const RouteLoader = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
)

// Wrapper component for lazy loaded routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<RouteLoader />}>{children}</Suspense>
)
import { EditSettingsView } from 'Features/Projects/Scenarios/EditSettings/EditSettings.view'
import { SalesRateView } from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/SalesRate.View'
import { ProductGroupingView } from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/ProductGrouping.View'
import { ClassificationView } from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/Classification.View'
import { ServiceLevelView } from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/ServiceLevel.View'
import { LocationGroupingView } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/LocationGrouping.View'
import { SiteStockFlowView } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/SiteStockFlow.View'
import { ReplenishmentView } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/Replenishment/Replenishment.View'
import { NetworkCapacitiesView } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/Capacities.View'
import { CapacitiesView } from 'Features/Projects/Scenarios/EditSettings/FactorySettings/Capacities.View'
import { AllocationsView } from 'Features/Projects/Scenarios/EditSettings/FactorySettings/Allocations.View'
import { ProductSettingsView } from 'Features/Projects/Scenarios/EditSettings/LineSettings/ProductSettings/ProductSettings.View'
import { ChangeoversView } from 'Features/Projects/Scenarios/EditSettings/LineSettings/Changeovers.View'
import { BatchSettingsView } from 'Features/Projects/Scenarios/EditSettings/LineSettings/BatchSettings/BatchSettings.View'
import { CreateScenarioPage } from 'Features/Projects/Scenarios/CreateScenario.page'
import { TemplateResultsView } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.view'
import { PlanTemplateSummaryView } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.view'
import { CreateProjectPage } from 'Features/Project/CreateProject/CreateProject.page'
import { OptimiserPage } from 'Features/Optimiser/Optimiser.page'
import { InventoryView } from 'Features/Projects/Scenarios/Inventory/Inventory.view'
import { InventoryResultsView } from 'Features/Projects/Scenarios/Inventory/Inventory.results.view'
import { ScenarioComparisonView } from 'Features/Projects/Analysis/ScenarioComparison.view'

export const breadcrumbRoutes = [
  { path: '/', breadcrumb: 'Home' },
  { path: '/projects', breadcrumb: 'Projects' },
  { path: '/projects/:projectId', breadcrumb: null },
  { path: '/projects/:projectId/scenarios', breadcrumb: 'Scenarios' },
  { path: '/projects/:projectId/scenarios/:scenarioId', breadcrumb: null },
  {
    path: '/projects/:projectId/scenarios/:scenarioId/scenario/template-summary',
    breadcrumb: 'Template Summary',
  },
]

const router = createBrowserRouter([
  {
    path: '/',
    element: <Shell />,
    children: [
      { path: 'tenants', element: <TenantListPage /> },
      { path: 'select-tenant', element: <TenantListPage /> },
      { index: true, element: <ProjectsPage /> },
      { path: 'projects', element: <ProjectsPage /> },
      { path: 'create-project', element: <CreateProjectPage /> },
      { path: 'optimiser', element: <OptimiserPage /> },
      {
        path: 'projects/:projectId',
        element: <ProjectLayout />,
        children: [
          {
            index: true,
            element: (
              <LazyRoute>
                <ScenariosView />
              </LazyRoute>
            ),
          },
          {
            path: 'scenarios',
            element: (
              <LazyRoute>
                <ScenariosView />
              </LazyRoute>
            ),
          },
          { path: 'create-scenario', element: <CreateScenarioPage /> },
          {
            path: 'scenarios/:scenarioId/scenario',
            element: <EditSettingsView />,
            children: [
              {
                index: true,
                element: <Navigate to="template-summary" replace />,
              },
              {
                path: 'template-summary',
                element: <PlanTemplateSummaryView />,
              },
              { path: 'sales-rate', element: <SalesRateView /> },
              { path: 'product-grouping', element: <ProductGroupingView /> },
              { path: 'classification', element: <ClassificationView /> },
              { path: 'service-level', element: <ServiceLevelView /> },
              { path: 'location-grouping', element: <LocationGroupingView /> },
              { path: 'site-stock-flow', element: <SiteStockFlowView /> },
              { path: 'replenishment', element: <ReplenishmentView /> },
              {
                path: 'network-capacities',
                element: <NetworkCapacitiesView />,
              },
              { path: 'capacities', element: <CapacitiesView /> },
              { path: 'allocations', element: <AllocationsView /> },
              { path: 'batch-production', element: <BatchSettingsView /> },
              { path: 'sku-production', element: <ProductSettingsView /> },
              { path: 'changeovers', element: <ChangeoversView /> },
              {
                path: 'template-summary/template-results/:workcentreId/:preferredTemplate/template',
                element: <TemplateResultsView />,
              },
              { path: 'inventory-summary', element: <InventoryView /> },
              {
                path: 'inventory-results/:siteKey',
                element: <InventoryResultsView />,
              },
            ],
          },
          {
            path: 'analysis',
            element: (
              <LazyRoute>
                <AnalysisView />
              </LazyRoute>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="scenario-comparison" replace />,
              },
              {
                path: 'scenario-comparison',
                element: <ScenarioComparisonView />,
              },
            ],
          },
          {
            path: 'approval',
            element: (
              <LazyRoute>
                <ApprovalView />
              </LazyRoute>
            ),
          },
          {
            path: 'snapshot',
            element: (
              <LazyRoute>
                <SnapshotView />
              </LazyRoute>
            ),
          },
        ],
      },
    ],
  },
])

export const AppRoutes = () => {
  return <RouterProvider router={router} />
}
